import {
  STOCK_TAKE_FINALISED_STORAGE_SCHEMA,
  STOCK_TAKE_MESSAGE_TYPE,
} from '../utils/stock-take-finalised-storage.constants';

/**
 * Backend contract for processing submitted stock takes (store service / cloud worker).
 *
 * ## Trigger
 * Document in `operational/stores_data/{storeId}/messages/from_app/{messageId}` with
 * `type: 'STOCK_TAKE'`.
 *
 * ## Payload
 * ```json
 * {
 *   "type": "STOCK_TAKE",
 *   "sender": "<user email>",
 *   "timestamp": "<Firestore Timestamp>",
 *   "payload": {
 *     "data": {
 *       "stockTakeId": "<stock take document id>",
 *       "finalisedStorageSchema": 2,
 *       "finalisedItemCount": 432
 *     }
 *   }
 * }
 * ```
 *
 * ## Stock take document (on SUBMITTED)
 * - `finalisedStorageSchema`: 2
 * - `finalisedItemCount`: number of counted lines written
 * - `status`: `SUBMITTED`
 *
 * ## Firestore layout
 * Path: `operational/stores_data/{storeId}/data/stock_takes/stock_takes/stock_takes/{stockTakeId}/`
 * ```
 * finalised_meta/_meta          — chunk index (itemCount, chunkDocIds, codeToChunkId, finalisedAt)
 * finalised_chunks/{chunkId}    — { schemaVersion: 2, entries: { [encodedCode]: IStockTakeFinalFigure } }
 * ```
 *
 * Each chunk targets ~750 KB estimated size. Entry keys use URI-encoded stock codes (`toStockDocId`).
 * Figure fields: `code`, `qtyCounted`, `varianceQty`, `variance`, `onhand`, `delColQty`, `movement`,
 * `delColMovement`, `latestCost`, `avCost`, `binLocation`, `randVariance`, `timesCounted`, `description`.
 *
 * ## Processing algorithm
 * 1. Load stock take doc; confirm `status === SUBMITTED` and `finalisedStorageSchema === 2`.
 * 2. Read `finalised_meta/_meta`; validate `itemCount` matches merged entry count if present.
 * 3. For each `chunkDocIds[]`, load `finalised_chunks/{chunkId}` and flatten `entries`.
 * 4. Apply adjustments per figure.
 * 5. Set stock take `status` to `SUCCESS` or `FAILED` with `errorMsg` as today.
 */
export interface IStockTakeFromAppMessagePayload {
  data: IStockTakeFromAppMessageData;
}

export interface IStockTakeFromAppMessageData {
  finalisedItemCount: number;
  finalisedStorageSchema: typeof STOCK_TAKE_FINALISED_STORAGE_SCHEMA;
  stockTakeId: string;
}

export interface IStockTakeFromAppMessage {
  payload: IStockTakeFromAppMessagePayload;
  sender: string;
  timestamp: unknown;
  type: typeof STOCK_TAKE_MESSAGE_TYPE;
}
